import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";

/**
 * Banner fixo no topo pedindo permissão para atualizar o app.
 * - Detecta nova versão via Service Worker (updatefound → statechange)
 * - Mostra banner sticky + toast persistente
 * - Botão "Atualizar agora" → SKIP_WAITING → controllerchange → reload
 * - Botão "Depois" → esconde o banner nesta sessão (mantém no sino 🔔)
 */
export function UpdatePrompt() {
  const [waitingWorker, setWaitingWorker] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const toastShownRef = useRef(false);
  const reloadingRef = useRef(false);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const showPrompt = (worker) => {
      setWaitingWorker(worker);
      if (!toastShownRef.current) {
        toastShownRef.current = true;
        toast.info("✨ Nova versão disponível!", {
          description: "Toque em 'Atualizar agora' no topo da tela para instalar.",
          duration: 8000,
        });
      }
    };

    navigator.serviceWorker.ready.then((registration) => {
      if (registration.waiting) showPrompt(registration.waiting);

      registration.addEventListener('updatefound', () => {
        const newWorker = registration.installing;
        if (!newWorker) return;
        newWorker.addEventListener('statechange', () => {
          if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
            showPrompt(newWorker);
          }
        });
      });

      // Check IMEDIATO ao carregar (pega updates deployed enquanto o app estava fechado)
      registration.update().catch(() => {});

      // Poll a cada 5 min para detectar update mesmo sem navegação
      const pollId = setInterval(() => {
        registration.update().catch(() => {});
      }, 5 * 60 * 1000);

      // Também checa quando o usuário volta pra aba (ex: fechou/abriu o PWA)
      const onVisible = () => {
        if (document.visibilityState === 'visible') {
          registration.update().catch(() => {});
        }
      };
      document.addEventListener('visibilitychange', onVisible);

      return () => {
        clearInterval(pollId);
        document.removeEventListener('visibilitychange', onVisible);
      };
    }).catch(() => {});

    const onControllerChange = () => {
      if (reloadingRef.current) return;
      reloadingRef.current = true;
      window.location.reload();
    };
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);
    return () => navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
  }, []);

  const handleUpdate = () => {
    if (waitingWorker) {
      try {
        waitingWorker.postMessage({ type: 'SKIP_WAITING' });
        toast.loading("Atualizando o app...", { duration: 3000 });
      } catch {
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    toast.message("Lembrete", {
      description: "A atualização ficará no sino 🔔 até você aplicar.",
      duration: 4000,
    });
  };

  if (!waitingWorker || dismissed) return null;

  return (
    <div
      data-testid="update-prompt-banner"
      className="fixed top-0 left-0 right-0 z-[10000] animate-slide-down"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      <div className="bg-gradient-to-r from-primary via-amber-500 to-primary text-black shadow-xl border-b-2 border-black/20">
        <div className="max-w-6xl mx-auto px-3 py-2.5 flex items-center gap-3">
          <div className="flex-shrink-0 text-2xl animate-pulse">✨</div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-sm sm:text-base leading-tight">Nova versão disponível!</p>
            <p className="text-xs opacity-80 leading-tight hidden sm:block">Atualize agora para receber os últimos ajustes.</p>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="update-now-btn"
              onClick={handleUpdate}
              className="bg-black text-primary font-bold px-3 py-1.5 sm:px-4 sm:py-2 rounded-lg text-xs sm:text-sm hover:bg-gray-900 transition active:scale-95"
            >
              Atualizar agora
            </button>
            <button
              data-testid="update-dismiss-btn"
              onClick={handleDismiss}
              className="text-black/70 hover:text-black text-xs sm:text-sm font-medium px-2"
              title="Depois"
            >
              Depois
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default UpdatePrompt;
